// Envío digital de un documento ya firmado (Fase 3/4 de "receta digital") —
// checkboxes WhatsApp/correo, deshabilitados con motivo si al paciente le
// falta ese contacto en el directorio local, resultado por canal. Genérico
// a propósito: lo usa tanto RecetaWizard.tsx/MedicamentosTab.tsx (recetas)
// como PanelFirmaImpresion.tsx (cualquier formulario firmado de las
// pestañas Documentos/Laboratorio y Estudios) — pedido explícito del
// usuario de duplicar el mismo botón ahí. No sabe ni le importa qué tipo de
// documento es: solo recibe el PDF ya firmado y una etiqueta legible.
import type { Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { Loader2 } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  enviarDocumentoDigital,
  obtenerPacienteVinculado,
  type CanalEnvioDocumento,
  type ResultadoCanalEnvio,
} from '../paciente/vaultClient';

export interface EnvioDigitalDocumentoProps {
  readonly subject: Reference<Patient>;
  readonly pdf: Uint8Array;
  readonly fileName: string;
  /** "Receta médica", "Resultado de laboratorio", "Certificado médico"... */
  readonly etiquetaDocumento: string;
  /** URL pública opcional (ej. `/farmacia/:token` de una receta) — ausente para documentos sin portal de validación propio. */
  readonly link?: string;
  readonly practitionerName?: string;
}

type EstadoContacto =
  | { fase: 'cargando' }
  | { fase: 'sin-contacto' }
  | { fase: 'listo'; cedula: string; phone: string | null; email: string | null }
  | { fase: 'error'; mensaje: string };

type EstadoEnvio =
  | { fase: 'idle' }
  | { fase: 'enviando' }
  | { fase: 'listo'; resultados: Partial<Record<CanalEnvioDocumento, ResultadoCanalEnvio>> }
  | { fase: 'error'; mensaje: string };

export function EnvioDigitalDocumento({ subject, pdf, fileName, etiquetaDocumento, link, practitionerName }: EnvioDigitalDocumentoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [contacto, setContacto] = useState<EstadoContacto>({ fase: 'cargando' });
  const [whatsapp, setWhatsapp] = useState(true);
  const [email, setEmail] = useState(true);
  const [envio, setEnvio] = useState<EstadoEnvio>({ fase: 'idle' });

  useEffect(() => {
    let cancelado = false;
    setContacto({ fase: 'cargando' });
    (async () => {
      const paciente = await medplum.readReference<Patient>(subject);
      const cedula = paciente.identifier?.[0]?.value;
      if (!cedula) {
        if (!cancelado) setContacto({ fase: 'sin-contacto' });
        return;
      }
      const vinculado = await obtenerPacienteVinculado(cedula);
      if (cancelado) return;
      if (!vinculado) {
        setContacto({ fase: 'sin-contacto' });
        return;
      }
      setContacto({ fase: 'listo', cedula, phone: vinculado.phone, email: vinculado.email });
    })().catch((err: unknown) => {
      if (!cancelado) setContacto({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
    });
    return () => {
      cancelado = true;
    };
  }, [medplum, subject]);

  async function enviar(): Promise<void> {
    if (contacto.fase !== 'listo') return;
    const canales: CanalEnvioDocumento[] = [
      ...(whatsapp && contacto.phone ? (['whatsapp'] as const) : []),
      ...(email && contacto.email ? (['email'] as const) : []),
    ];
    if (canales.length === 0) return;
    setEnvio({ fase: 'enviando' });
    try {
      const resultado = await enviarDocumentoDigital({
        cedula: contacto.cedula,
        canales,
        pdf,
        fileName,
        etiquetaDocumento,
        link,
        practitionerName,
      });
      setEnvio({ fase: 'listo', resultados: resultado.resultados });
    } catch (err) {
      setEnvio({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
    }
  }

  if (contacto.fase === 'cargando') {
    return <p className="text-sm text-muted-foreground">{t('envioDigital.buscandoContacto')}</p>;
  }
  if (contacto.fase === 'sin-contacto') {
    return (
      <Alert>
        <AlertDescription>{t('envioDigital.sinContacto')}</AlertDescription>
      </Alert>
    );
  }
  if (contacto.fase === 'error') {
    return (
      <Alert variant="red">
        <AlertDescription>{contacto.mensaje}</AlertDescription>
      </Alert>
    );
  }

  const puedeWhatsapp = Boolean(contacto.phone);
  const puedeEmail = Boolean(contacto.email);
  const puedeEnviar = (whatsapp && puedeWhatsapp) || (email && puedeEmail);

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <p className="text-xs font-mono uppercase tracking-wide text-hce-muted">{t('envioDigital.titulo')}</p>
      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={whatsapp && puedeWhatsapp} disabled={!puedeWhatsapp} onCheckedChange={(v) => setWhatsapp(v === true)} />
          {t('envioDigital.whatsapp')}
          {!puedeWhatsapp && <span className="text-xs text-hce-muted">({t('envioDigital.sinTelefono')})</span>}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={email && puedeEmail} disabled={!puedeEmail} onCheckedChange={(v) => setEmail(v === true)} />
          {t('envioDigital.correo')}
          {!puedeEmail && <span className="text-xs text-hce-muted">({t('envioDigital.sinCorreo')})</span>}
        </label>
      </div>
      <Button type="button" size="sm" className="w-fit" disabled={!puedeEnviar || envio.fase === 'enviando'} onClick={() => void enviar()}>
        {envio.fase === 'enviando' ? (
          <>
            <Loader2 className="size-4 animate-spin" /> {t('envioDigital.enviando')}
          </>
        ) : (
          t('envioDigital.boton')
        )}
      </Button>
      {envio.fase === 'listo' && (
        <div className="flex flex-col gap-1">
          {envio.resultados.whatsapp && (
            <Alert variant={envio.resultados.whatsapp.status === 'error' ? 'red' : 'default'}>
              <AlertDescription>
                {t('envioDigital.whatsapp')}: {envio.resultados.whatsapp.detail}
              </AlertDescription>
            </Alert>
          )}
          {envio.resultados.email && (
            <Alert variant={envio.resultados.email.status === 'error' ? 'red' : 'default'}>
              <AlertDescription>
                {t('envioDigital.correo')}: {envio.resultados.email.detail}
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}
      {envio.fase === 'error' && (
        <Alert variant="red">
          <AlertDescription>{envio.mensaje}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
